import { Model, modelRegistry } from './Model';
import { Client, PriceCalculationItem } from '.';
import Printable from '../print/Printable';
import { documentBlocks, documentHeader, documentSignatures, documentSpacer, documentTable, factLine } from '../print/DocumentLayout';
import Money from '../utilities/Money';
import { I18n } from '../wrappers/I18n';
import Store from '../wrappers/Store';
import moment from 'moment-timezone';
export class PriceCalculation extends Model {
    static entity = 'price_calculations';
    id = null;
    user_id = null;
    number = null;
    date = null;
    client_id = null;
    invoice_number = null;
    invoice_date = null;
    invoice_due_date = null;
    invoice_paid_date = null;
    invoice_price_total = '0.00';
    invoice_with_tax = true;
    dependent_costs_total = '0.0000';
    description = null;
    recorded_at = null;
    created_at = null;
    updated_at = null;
    items = [];
    client;
    static fields() {
        return {
            id: this.attr(null),
            user_id: this.attr(null),
            number: this.attr(null),
            date: this.attr(null),
            client_id: this.attr(null),
            invoice_number: this.attr(null),
            invoice_date: this.attr(null),
            invoice_due_date: this.attr(null),
            invoice_paid_date: this.attr(null),
            invoice_price_total: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            invoice_with_tax: this.attr(true, value => !!value),
            dependent_costs_total: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            description: this.attr(null),
            recorded_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            items: this.attr([], (value) => (value || []).map(item => new PriceCalculationItem(item))),
            client: this.attr(undefined, value => value ? new Client(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getInvoicePriceTotal() {
        return Money.parse(this.invoice_price_total);
    }
    getDependentCostsTotal() {
        return Money.parse(this.dependent_costs_total);
    }
    getInvoiceValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getInvoiceValue()), Money.parse(0));
    }
    getDependentCosts() {
        return this.items.reduce((acc, cur) => acc.add(cur.getDependentCosts()), Money.parse(0));
    }
    getPurchaseValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseValue()), Money.parse(0));
    }
    getPurchaseTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseTax()), Money.parse(0));
    }
    getPurchaseValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPurchaseValueAfterTax()), Money.parse(0));
    }
    getPriceDifference() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPriceDifference()), Money.parse(0));
    }
    getSellingValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValue()), Money.parse(0));
    }
    getTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
    }
    getTaxesPerRate() {
        const taxes = {};
        this.items.forEach(item => {
            if (taxes[item.tax_rate]) {
                taxes[item.tax_rate].value = taxes[item.tax_rate].value.add(item.getTax());
                taxes[item.tax_rate].priceBeforeTax = taxes[item.tax_rate].priceBeforeTax.add(item.getSellingValue());
            }
            else {
                taxes[item.tax_rate] = {
                    value: item.getTax(),
                    priceBeforeTax: item.getSellingValue(),
                };
            }
        });
        return Object.keys(taxes).map(rate => {
            return {
                rate: rate,
                value: taxes[rate].value,
                priceBeforeTax: taxes[rate].priceBeforeTax,
            };
        });
    }
    getSellingValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0));
    }
    getPrintable() {
        const pr = new Printable();
        const venue = Store.get('tenant')?.venue;
        if (!venue)
            return pr;
        const date = (value) => value ? moment(value).format('DD.MM.YYYY.') : null;
        documentHeader(pr, {
            title: I18n.t('price_calculations.heading'),
            facts: factLine([I18n.t('price_calculations.number'), this.number], [I18n.t('price_calculations.date'), date(this.date)], [I18n.t('price_calculations.description'), this.description]),
            venueName: venue.name,
            venueLine: `${I18n.t('general.tax_id_number')}: ${venue.tax_id_number}`,
        });
        documentBlocks(pr, {
            left: {
                title: I18n.t('price_calculations.section_supplier_invoice'),
                lines: [
                    factLine([I18n.t('general.supplier'), this.client?.name]),
                    factLine([I18n.t('price_calculations.invoice_number'), this.invoice_number], [I18n.t('price_calculations.invoice_date'), date(this.invoice_date)]),
                    factLine([I18n.t('price_calculations.invoice_due_date'), date(this.invoice_due_date)], [I18n.t('price_calculations.invoice_paid_date'), date(this.invoice_paid_date)]),
                    factLine([I18n.t('price_calculations.invoice_value'), this.getInvoiceValue().toFormat('0.00')]),
                    factLine([I18n.t('price_calculations.dependent_costs'), this.getDependentCosts().toFormat('0.00')]),
                ],
            },
            right: {
                title: I18n.t('price_calculations.section_summary'),
                pairs: [
                    [I18n.t('price_calculations.purchase_value'), this.getPurchaseValue().toFormat('0.00')],
                    [I18n.t('price_calculations.purchase_tax'), this.getPurchaseTax().toFormat('0.00')],
                    [I18n.t('price_calculations.purchase_value_after_tax'), this.getPurchaseValueAfterTax().toFormat('0.00')],
                    [I18n.t('price_calculations.price_difference'), this.getPriceDifference().toFormat('0.00')],
                    [I18n.t('price_calculations.selling_value'), this.getSellingValue().toFormat('0.00')],
                    [I18n.t('price_calculations.tax'), this.getTax().toFormat('0.00')],
                    [I18n.t('price_calculations.selling_value_after_tax'), this.getSellingValueAfterTax().toFormat('0.00')],
                ],
            },
        });
        documentSpacer(pr);
        const figure = (key) => ({ label: I18n.t(`price_calculations.${key}`), kind: 'figure' });
        const columns = [
            { label: I18n.t('general.number'), kind: 'index' },
            { label: I18n.t('general.code'), kind: 'code' },
            { label: I18n.t('general.inventory_item'), kind: 'name' },
            figure('quantity'),
            figure('invoice_price'),
            figure('invoice_discount_percent'),
            figure('invoice_price_after_discount'),
            figure('invoice_value'),
            figure('dependent_costs'),
            figure('purchase_value'),
            figure('price_difference'),
            figure('selling_value'),
            { label: `${I18n.t('price_calculations.tax_rate')} %`, kind: 'figure' },
            figure('tax'),
            figure('selling_value_after_tax'),
            figure('purchase_price'),
            figure('margin_percent'),
            figure('price_difference_per_unit'),
            figure('selling_price'),
            figure('selling_price_after_tax'),
        ];
        const rows = this.items.map((item, index) => [
            String(index + 1),
            item.getProduct()?.code || '',
            item.getProduct()?.name || '',
            `${item.getQuantity()} ${item.getUnitOfMeasure()}`,
            item.getInvoicePrice().toFormat('0.00'),
            item.getInvoiceDiscountPercent() + '',
            item.getInvoicePriceAfterDiscount().toFormat('0.00'),
            item.getInvoiceValue().toFormat('0.00'),
            item.getDependentCosts().toFormat('0.00'),
            item.getPurchaseValue().toFormat('0.00'),
            item.getPriceDifference().toFormat('0.00'),
            item.getSellingValue().toFormat('0.00'),
            item.getTaxRate() + '',
            item.getTax().toFormat('0.00'),
            item.getSellingValueAfterTax().toFormat('0.00'),
            item.getPurchasePrice().toFormat('0.00'),
            item.getMarginPercent() + '',
            item.getPriceDifferencePerUnit().toFormat('0.00'),
            item.getSellingPrice().toFormat('0.00'),
            item.getSellingPriceAfterTax().toFormat('0.00'),
        ]);
        documentTable(pr, columns, rows, {
            density: 'dense',
            recap: this.getTaxesPerRate().flatMap((tax) => [
                [`${I18n.t('price_calculations.prica_before_tax')} ${parseFloat(tax.rate)}%`, tax.priceBeforeTax.toFormat('0.00')],
                [`${I18n.t('price_calculations.tax_rate')} ${parseFloat(tax.rate)}%`, tax.value.toFormat('0.00')],
            ]),
        });
        documentSignatures(pr, [I18n.t('general.created_by'), I18n.t('general.received_by'), I18n.t('general.responsible_person')]);
        return pr;
    }
}
export default PriceCalculation;
modelRegistry.register('PriceCalculation', PriceCalculation);
//# sourceMappingURL=PriceCalculation.js.map